// useShoppingRegion — user's shopping locality preference.
//
// Brand suggestions / receipt OCR / photo-capture priors all benefit
// from knowing where the user shops. A Chicago user picking
// "italian beef" should see Vienna Beef ranked above Boar's Head;
// a Berlin user picking "wurst" should see Augustiner / Wagner /
// regional German bier brands, not Aldi US.
//
// The region is a hierarchical string so coarse / fine filters
// both work without a custom lookup table:
//
//   "us"                        — anywhere in the US
//   "us-midwest"                — Midwest region
//   "us-midwest-chicago"        — specifically Chicago metro
//   "de"                        — anywhere in Germany
//   "de-bavaria-munich"         — Munich
//   "international"             — fallback / no region preference
//
// Filter check is a string-prefix match: a brand with
// regions: ["us-midwest"] matches users in "us-midwest" AND
// "us-midwest-chicago" AND "us". A brand with regions: ["us"]
// matches every us-* user. Brands with scope: "international"
// match everyone.
//
// Persistence:
//   • localStorage today (per-device, no sync)
//   • TODO: graduate to profiles.shopping_region column when we
//     want cross-device sync. The hook's API stays identical;
//     only the read/write internals change.
//
// Default: "us" — most likely starting point for an MVP user
// without geo-IP plumbed yet. Override per-user via Settings UI
// (not built — wire when needed).

import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "mise:shopping_region:v1";
const DEFAULT_REGION = "us";

// Cross-tab broadcast so flipping the region in one tab updates
// every BrandStep / wizard / picker without a reload.
const listeners = new Set();
function broadcast(value) {
  for (const fn of listeners) {
    try { fn(value); } catch (_e) { /* listener threw, drop it */ }
  }
}

function readStored() {
  if (typeof window === "undefined") return DEFAULT_REGION;
  try {
    return window.localStorage?.getItem(STORAGE_KEY) || DEFAULT_REGION;
  } catch { return DEFAULT_REGION; }
}

export function useShoppingRegion() {
  const [region, setRegion] = useState(readStored);

  useEffect(() => {
    const fn = (next) => setRegion(next);
    listeners.add(fn);
    // Listen for storage events from OTHER tabs / windows. Same-tab
    // updates go through our listener set above.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setRegion(e.newValue || DEFAULT_REGION);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("storage", onStorage);
    }
    return () => {
      listeners.delete(fn);
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", onStorage);
      }
    };
  }, []);

  const update = useCallback((next) => {
    const value = (typeof next === "string" && next.trim()) || DEFAULT_REGION;
    if (typeof window !== "undefined") {
      try { window.localStorage?.setItem(STORAGE_KEY, value); } catch { /* quota */ }
    }
    setRegion(value);
    broadcast(value);
  }, []);

  return [region, update];
}

// Imperative read for non-React callers (e.g. brand-population
// preview scripts, receipt OCR resolver).
export function getShoppingRegion() {
  return readStored();
}

// Sync the region from the profile's shopping_cities at boot. This
// used to live inside Settings' PreferencesSection, which meant a
// user who set cities during onboarding (or on another device) kept
// a stale "us" region until they happened to OPEN Settings — brand
// pickers stayed generic the whole time. Call from App at profile
// load; idempotent, cheap.
export function syncRegionFromProfile(profile) {
  const cities = Array.isArray(profile?.shopping_cities)
    ? profile.shopping_cities
    : [];
  const primary = cities[0] || null;
  if (!primary) return;
  if (readStored() === primary) return;
  if (typeof window !== "undefined") {
    try { window.localStorage?.setItem(STORAGE_KEY, primary); } catch { /* quota */ }
  }
  broadcast(primary);
}

// Region matching — a brand "matches" the user's region if any of
// the brand's region tags is a prefix of the user's region OR vice
// versa. So:
//
//   user "us-midwest-chicago"
//     matches brand regions: ["us"]               ✓ (brand broader)
//     matches brand regions: ["us-midwest"]       ✓ (brand broader)
//     matches brand regions: ["us-midwest-chicago"] ✓ (exact)
//     matches brand regions: ["us-midwest-stl"]   ✗ (sibling, not prefix)
//
// Brands with scope: "international" or no region tags match every
// user (treated as "no constraint").
export function brandMatchesRegion(brand, userRegion) {
  if (!brand) return false;
  if (brand.scope === "international") return true;
  const regions = Array.isArray(brand.regions) ? brand.regions : null;
  if (!regions || regions.length === 0) return true;
  const u = (userRegion || DEFAULT_REGION).toLowerCase();
  for (const r of regions) {
    if (typeof r !== "string") continue;
    const b = r.toLowerCase();
    if (u === b) return true;
    if (u.startsWith(b + "-")) return true; // brand broader than user
    if (b.startsWith(u + "-")) return true; // brand more specific (still surface — user might travel)
  }
  return false;
}
